import type { FC, MouseEvent } from 'react'
import { useState } from 'react'

import { Checkbox, Input, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import ArrowBackIcon from 'mdi-react/ArrowBackIcon'
import EditIcon from 'mdi-react/EditIcon'

import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'

import type { CustomNumberStateType, NumberRecordType, PhoneBookType } from '../types'

type Props = {
  scrollToNextSlide: (index: number) => void
  selectedPhoneBook: PhoneBookType
  selectedNumbers: Map<string, NumberRecordType>
  onSelectNumberHandler: (numbers: Map<string, NumberRecordType>) => void
  onCustomNumber: (state: CustomNumberStateType | undefined) => void
}

export const PhoneNumbers: FC<Props> = ({
  scrollToNextSlide,
  selectedPhoneBook,
  onSelectNumberHandler,
  selectedNumbers,
  onCustomNumber,
}) => {
  const [searchPhrase, setSearchPhrase] = useState('')

  const onClickNumber = (fullNumber: string, item: NumberRecordType) => {
    if (item.countryId === null) return
    const map = new Map()
    map.set(fullNumber, item)
    onSelectNumberHandler(map)
  }

  const areAllSelected = () =>
    // We spread the map here because datatype Map does not have a .map function like Arrays
    ![...selectedPhoneBook.numbers!].some(
      ([number, item]) => !selectedNumbers.has(number) && item.countryId
    )

  const validNumbersCount = () =>
    // We spread the map here because datatype Map does not have a .map function like Arrays
    [...selectedPhoneBook.numbers!].reduce((total, [, item]) => {
      if (item.countryId) {
        return total + 1
      }
      return total
    }, 0)

  const onSelectAll = (e: MouseEvent) => {
    e.preventDefault()

    const numbers = new Map()

    const bool = areAllSelected()

    if (bool) {
      selectedPhoneBook.numbers!.forEach((item, number) => {
        if (item.countryId) {
          numbers.set(number, item)
        }
      })
    } else {
      selectedPhoneBook.numbers!.forEach((item, number) => {
        if (!(selectedNumbers.has(number) && item.countryId)) {
          numbers.set(number, item)
        }
      })
    }

    onSelectNumberHandler(numbers)
  }

  if (!selectedPhoneBook?.numbers) return null

  return (
    <div className="flex w-full flex-shrink-0 snap-start flex-col md:px-5 lg:w-2/5">
      {/* SearchBar */}
      <div className="flex w-full flex-col">
        <div className="flex h-12 items-center pb-2 md:justify-between">
          <ArrowBackIcon onClick={() => scrollToNextSlide(0)} className="mr-3 block lg:hidden" />
          <h3 className="mb-0 lg:leading-10">{selectedPhoneBook.name}</h3>
          {(selectedPhoneBook.type === 'group' || selectedPhoneBook.type === 'phone-book') && (
            <Button onClick={() => onCustomNumber({})}>
              <Translation id="Add new number" />
            </Button>
          )}
        </div>
        <div className="grow-0">
          <Input
            isFullWidth
            id="search"
            marginBottom={16}
            onChange={e => setSearchPhrase(e.target.value?.toLowerCase() || '')}
          />
        </div>
      </div>
      {/* Body */}
      <div className="w-full grow overflow-y-auto">
        {/* We spread the map here because datatype Map does not have a .map function like Arrays */}
        {[...selectedPhoneBook.numbers].map(([fullNumber, item]) => {
          const { name, countryId, number, id } = item
          let numberL = number.toLowerCase()
          const nameL = name?.toLowerCase() || ''
          const countryString = '+' + (countryId?.toString()?.toLowerCase() || '???')

          if (
            !numberL.includes(searchPhrase) &&
            !nameL.includes(searchPhrase) &&
            !countryString.includes(searchPhrase)
          )
            return null
          return (
            <div
              key={fullNumber}
              className="flex w-full cursor-pointer justify-between gap-1 rounded p-2 hover:bg-gray-50"
            >
              <div
                onClick={() => onClickNumber(fullNumber, item)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onClickNumber(fullNumber, item)
                  }
                }}
                className={`grow ${!countryId ? 'text-red-600' : ''}`}
              >
                <div>
                  <StyledText size="regular-plus">
                    <Highlight pattern={searchPhrase} text={countryString} />
                  </StyledText>{' '}
                  <Highlight pattern={searchPhrase} text={numberL} />
                </div>
                <span className="text-sm text-gray-400">
                  <Highlight pattern={searchPhrase} text={name} />
                </span>
              </div>
              {['group', 'phone-book'].includes(selectedPhoneBook.type) && (
                <EditIcon
                  onClick={() =>
                    onCustomNumber({
                      id,
                      name,
                      countryId,
                      number,
                      type: selectedPhoneBook.type,
                    })
                  }
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onCustomNumber({ id, name, countryId, number })
                    }
                  }}
                  size={20}
                  className="hover:text-primary"
                />
              )}
              <Checkbox
                name="checkbox"
                checked={selectedNumbers.has(fullNumber)}
                id={fullNumber}
                className={countryString ? '' : 'invisible'}
                marginTop={0}
                marginLeft={0}
                onChange={() => onClickNumber(fullNumber, item)}
              />
            </div>
          )
        })}
      </div>

      <div className="flex flex-col">
        {validNumbersCount() > 0 && (
          <Button className="min-h-[2.5rem]" onClick={onSelectAll}>
            <Translation id={areAllSelected() ? 'Deselect All' : 'Select all'} />
          </Button>
        )}
        <Button
          variant="primary"
          className="min-h-[2.5rem] lg:hidden"
          onClick={() => scrollToNextSlide(2)}
        >
          <Translation id="Show selected" />
        </Button>
      </div>
    </div>
  )
}
